import { FlightsClassTypes } from 'constants/flight'
import {
  MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG,
  MALDIVES_CHARTER_FARE_TAG_BUSINESS,
  MALDIVES_CHARTER_FARE_TAG_ECONOMY,
  MALDIVES_CHARTER_FARE_TAG_ECONOMY_PLUS,
  MALDIVES_CHARTER_FARE_TAG_SKYLOUNGE,
} from 'constants/charterFlight'
import { getCharterFareCabinLabel } from 'hooks/Flights/useCharterFlightLabels'
import { isSkyLoungeCompatible } from 'lib/flights/flightUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import { arrayToStringListFormat } from 'lib/string/stringUtils'
import type { IntlShape } from 'react-intl'

const CHARTER_FARE_CLASS_RANK = [
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.PREMIUM,
  FlightsClassTypes.LUXROW,
  FlightsClassTypes.BUSINESS,
]

const CHARTER_FARE_TAG_TO_CLASS: Record<string, FlightsClassTypes> = {
  [MALDIVES_CHARTER_FARE_TAG_ECONOMY]: FlightsClassTypes.ECONOMY,
  [MALDIVES_CHARTER_FARE_TAG_ECONOMY_PLUS]: FlightsClassTypes.PREMIUM,
  [MALDIVES_CHARTER_FARE_TAG_SKYLOUNGE]: FlightsClassTypes.LUXROW,
  [MALDIVES_CHARTER_FARE_TAG_BUSINESS]: FlightsClassTypes.BUSINESS,
}

const CLASS_SUFFIX_FARE_CLASSES = new Set<FlightsClassTypes>([
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.BUSINESS,
])

function getCharterFareCabinBannerLabel(intl: IntlShape, fareClass: FlightsClassTypes): string {
  switch (fareClass) {
    case FlightsClassTypes.ECONOMY:
      return 'economy'
    case FlightsClassTypes.BUSINESS:
      return 'business'
    default:
      return getCharterFareCabinLabel(intl, fareClass)
  }
}

export const ALL_CHARTER_FARE_CLASSES = CHARTER_FARE_CLASS_RANK

export function getDefaultFareClassFromAllowed(allowedFareClasses: Array<FlightsClassTypes>): FlightsClassTypes | undefined {
  return CHARTER_FARE_CLASS_RANK.find(fareClass => allowedFareClasses.includes(fareClass))
}

export function getOrderedCharterFareClasses(allowedFareClasses: Array<FlightsClassTypes>): Array<FlightsClassTypes> {
  return CHARTER_FARE_CLASS_RANK.filter(fareClass => allowedFareClasses.includes(fareClass))
}

export function formatCharterFareClassList(allowedFareClasses: Array<FlightsClassTypes>, intl: IntlShape): string {
  const orderedFareClasses = getOrderedCharterFareClasses(allowedFareClasses)
  if (orderedFareClasses.length === 0) {
    return ''
  }
  const lastFareClass = orderedFareClasses[orderedFareClasses.length - 1]
  const labels = orderedFareClasses.map((fareClass) => {
    const baseLabel = getCharterFareCabinLabel(intl, fareClass)
    if (fareClass === lastFareClass && CLASS_SUFFIX_FARE_CLASSES.has(fareClass)) {
      return intl.formatMessage(
        {
          id: 'O-nINLKqHpaPPyk4uiio',
          defaultMessage: '{baseLabel} class',
        },
        { baseLabel },
      )
    }
    return baseLabel
  })
  return arrayToStringListFormat(labels, 'and', intl)
}

export function formatCharterFareClassListForBanner(allowedFareClasses: Array<FlightsClassTypes>, intl: IntlShape): string {
  const orderedFareClasses = getOrderedCharterFareClasses(allowedFareClasses)
  if (orderedFareClasses.length === 0) {
    return ''
  }
  const lastFareClass = orderedFareClasses[orderedFareClasses.length - 1]
  const labels = orderedFareClasses.map((fareClass) => {
    const baseLabel = getCharterFareCabinBannerLabel(intl, fareClass)
    if (fareClass === lastFareClass && CLASS_SUFFIX_FARE_CLASSES.has(fareClass)) {
      return intl.formatMessage(
        {
          id: 'ypyAnb3lOknxKEVcjaWD',
          defaultMessage: '{baseLabel} class',
        },
        { baseLabel },
      )
    }
    return baseLabel
  })
  return arrayToStringListFormat(labels, 'and', intl)
}

export interface CharterFareClassPolicyInput {
  campaignTags?: Array<string>
  occupancies?: Array<App.Occupants>
}

export interface CharterFareClassPolicyResult {
  isCharterOffer: boolean
  taggedFareClasses: Array<FlightsClassTypes>
  allowedFareClasses: Array<FlightsClassTypes>
  defaultFareClass?: FlightsClassTypes
}

export function getCharterFareClassPolicy({
  campaignTags = EmptyArray,
  occupancies,
}: CharterFareClassPolicyInput): CharterFareClassPolicyResult {
  const isCharterOffer = campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  if (!isCharterOffer) {
    return {
      isCharterOffer: false,
      taggedFareClasses: EmptyArray,
      allowedFareClasses: EmptyArray,
      defaultFareClass: undefined,
    }
  }

  const taggedFareClasses = campaignTags
    .map(tag => CHARTER_FARE_TAG_TO_CLASS[tag])
    .filter((value): value is FlightsClassTypes => Boolean(value))

  const baseAllowedFareClasses = taggedFareClasses.length > 0
    ? taggedFareClasses
    : ALL_CHARTER_FARE_CLASSES

  const allowsLuxRow = !occupancies || isSkyLoungeCompatible(occupancies)
  const allowedFareClasses = allowsLuxRow
    ? baseAllowedFareClasses
    : baseAllowedFareClasses.filter(fareClass => fareClass !== FlightsClassTypes.LUXROW)

  const defaultFareClass = getDefaultFareClassFromAllowed(allowedFareClasses)

  return {
    isCharterOffer,
    taggedFareClasses,
    allowedFareClasses,
    defaultFareClass,
  }
}

export function resolveAllowedFareClass({
  selectedFareClass,
  campaignTags,
  occupancies,
}: {
  selectedFareClass?: FlightsClassTypes
  campaignTags?: Array<string>
  occupancies?: Array<App.Occupants>
}): FlightsClassTypes | undefined {
  const { allowedFareClasses, defaultFareClass } = getCharterFareClassPolicy({
    campaignTags,
    occupancies,
  })

  if (selectedFareClass && allowedFareClasses.includes(selectedFareClass)) {
    return selectedFareClass
  }

  return defaultFareClass
}

export function getUnionAllowedFareClasses({
  offers,
  occupancies,
}: {
  offers: Array<{ campaignTags?: Array<string> }>
  occupancies?: Array<App.Occupants>
}): Array<FlightsClassTypes> {
  const union = new Set<FlightsClassTypes>()

  offers.forEach((offer) => {
    const policy = getCharterFareClassPolicy({
      campaignTags: offer.campaignTags ?? EmptyArray,
      occupancies,
    })

    policy.allowedFareClasses.forEach(fareClass => union.add(fareClass))
  })

  return CHARTER_FARE_CLASS_RANK.filter(fareClass => union.has(fareClass))
}
